import { useMemo, useRef } from "react";
import { useVehiclesSocketStore } from "./vehiclesSocketStore";
import { useCompaniesStore } from "./companiesStore";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import { useGroupsStore } from "./groupsStore";
import type { VehicleSocket } from "./vehiclesSocketStore";
import { useShallow } from "zustand/react/shallow";
import useIsMonitor from "../../hooks/useIsMonitor";
import { useUserStore } from "../userStore";

// Función para comparar si dos arrays de vehículos son visualmente iguales
// Solo compara propiedades que afectan el renderizado de los marcadores
const areVehiclesVisuallyEqual = (
  prev: VehicleSocket[],
  next: VehicleSocket[]
): boolean => {
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i++) {
    const p = prev[i];
    const n = next[i];
    if (
      p.id !== n.id ||
      p.latitude !== n.latitude ||
      p.longitude !== n.longitude ||
      p.status !== n.status ||
      // El modelo del garaje también se ve: sin esto el cambio esperaba al
      // siguiente reporte con movimiento (medido: ~4 s en una unidad parada).
      p.vehicle?.pointerModel !== n.vehicle?.pointerModel
    ) {
      return false;
    }
  }
  return true;
};

export const useFilteredSocketVehicles = () => {
  const vehicles = useVehiclesSocketStore((state) => state.vehicles);
  const searchQuery = useVehiclesSocketStore(
    (state) => state.debouncedSearchQuery
  );
  const selectedStatus = useVehiclesSocketStore(
    (state) => state.selectedStatus
  );
  const selectedGroup = useVehiclesSocketStore(
    (state) => state.selectedGroup
  );
  const selectedCompany = useVehiclesSocketStore(
    (state) => state.selectedCompany
  );
  const companyUser = useCompaniesStore((state) => state.companyUser);
  const user = useUserStore((state) => state.user);
  const userRoleId = user?.role_id || localStorage.getItem("userRoleId") || "";
  const isReseller = userRoleId === ADMIN_RESELLER_ROLE_ID;
  const groups = useGroupsStore((state) => state.groups);
  const { isMonitor, devices: monitorDeviceIds } = useIsMonitor();

  // Referencia para mantener el array anterior y evitar re-renders innecesarios
  const prevResultRef = useRef<VehicleSocket[]>([]);

  const filteredVehicles = useMemo(() => {
    let result = vehicles;

    // Si es monitor, mostrar dispositivos asignados directamente o por grupos
    if (isMonitor) {
      // Obtener dispositivos de grupos donde el monitorista está asignado
      const monitoristaUserId = localStorage.getItem("userId");
      const monitoristaGroups = groups.filter((g) =>
        g.monitorista_ids?.includes(monitoristaUserId || "")
      );
      const groupDeviceIds = monitoristaGroups.flatMap((g) => g.devices_ids || []);

      // Combinar dispositivos asignados directamente con dispositivos de grupos
      const allowedDeviceIds = new Set([
        ...(monitorDeviceIds || []),
        ...groupDeviceIds,
      ]);

      // Si tiene dispositivos permitidos, filtrar; si no, mostrar vacío
      if (allowedDeviceIds.size > 0) {
        result = result.filter((vehicle) => allowedDeviceIds.has(vehicle.id));
      } else {
        result = [];
      }
    }

    // Si el usuario es reseller, filtrar por la compañía seleccionada
    if (isReseller) {
      // Si no hay compañía seleccionada, no mostrar vehículos
      if (!selectedCompany) {
        return [];
      }
      result = result.filter((vehicle) => vehicle.companyId === selectedCompany);
    } else if (companyUser) {
      // Si el usuario es carrier (no reseller), solo ve sus vehículos
      result = result.filter((vehicle) => vehicle.companyId === companyUser);
    }

    // Filtrar por grupo (usando devices_ids del grupo)
    if (selectedGroup !== "all") {
      const selectedGroupData = groups.find((g) => g.id === selectedGroup);
      const groupDeviceIds = selectedGroupData?.devices_ids || [];
      result = result.filter((vehicle) => groupDeviceIds.includes(vehicle.id));
    }

    // Filtrar por estado
    if (selectedStatus) {
      result = result.filter((vehicle) => vehicle.status === selectedStatus);
    }

    // Filtrar por búsqueda
    if (searchQuery.trim()) {
      const normalizedQuery = searchQuery.toLowerCase().trim();
      const searchTerms = normalizedQuery.split(/\s+/);

      result = result.filter((vehicle) => {
        const searchableFields = [
          vehicle.id,
          vehicle.fullId,
          vehicle.imei,
          vehicle.vehicle?.plates,
          vehicle.vehicle?.brand,
          vehicle.vehicle?.model,
          vehicle.vehicle?.name,
          vehicle.driver?.name,
          vehicle.driver?.email,
          vehicle.driver?.phone,
          vehicle.group,
          vehicle.address,
        ];

        const searchableText = searchableFields
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchTerms.every((term) => searchableText.includes(term));
      });
    }

    // Comparar con el resultado anterior para evitar re-renders innecesarios
    // Si los vehículos son visualmente iguales, retornar la referencia anterior
    if (areVehiclesVisuallyEqual(prevResultRef.current, result)) {
      return prevResultRef.current;
    }

    prevResultRef.current = result;
    return result;
  }, [vehicles, searchQuery, selectedStatus, selectedGroup, selectedCompany, companyUser, isReseller, groups, isMonitor, monitorDeviceIds]);

  return filteredVehicles;
};

// Selector para obtener vehículos base filtrados solo por compañía (para calcular grupos)
export const useCompanyFilteredSocketVehicles = () => {
  const vehicles = useVehiclesSocketStore((state) => state.vehicles);
  const selectedCompany = useVehiclesSocketStore(
    (state) => state.selectedCompany
  );
  const companyUser = useCompaniesStore((state) => state.companyUser);
  const user = useUserStore((state) => state.user);
  const userRoleId = user?.role_id || localStorage.getItem("userRoleId") || "";
  const isReseller = userRoleId === ADMIN_RESELLER_ROLE_ID;
  const groups = useGroupsStore((state) => state.groups);
  const { isMonitor, devices: monitorDeviceIds } = useIsMonitor();

  const companyVehicles = useMemo(() => {
    let result = vehicles;

    // Si es monitor, mostrar dispositivos asignados directamente o por grupos
    if (isMonitor) {
      // Obtener dispositivos de grupos donde el monitorista está asignado
      const monitoristaUserId = localStorage.getItem("userId");
      const monitoristaGroups = groups.filter((g) =>
        g.monitorista_ids?.includes(monitoristaUserId || "")
      );
      const groupDeviceIds = monitoristaGroups.flatMap((g) => g.devices_ids || []);

      // Combinar dispositivos asignados directamente con dispositivos de grupos
      const allowedDeviceIds = new Set([
        ...(monitorDeviceIds || []),
        ...groupDeviceIds,
      ]);

      // Si tiene dispositivos permitidos, filtrar; si no, mostrar vacío
      if (allowedDeviceIds.size > 0) {
        result = result.filter((vehicle) => allowedDeviceIds.has(vehicle.id));
      } else {
        result = [];
      }
    }

    // Si el usuario es reseller, filtrar por la compañía seleccionada
    if (isReseller) {
      if (!selectedCompany) {
        return [];
      }
      result = result.filter((vehicle) => vehicle.companyId === selectedCompany);
    } else if (companyUser) {
      // Si el usuario es carrier (no reseller), solo ve sus vehículos
      result = result.filter((vehicle) => vehicle.companyId === companyUser);
    }

    return result;
  }, [vehicles, selectedCompany, companyUser, isReseller, isMonitor, monitorDeviceIds]);

  return companyVehicles;
};

// Selector para obtener estadísticas (sin filtros aplicados, solo por grupo)
export const useSocketVehicleStats = () => {
  const vehicles = useVehiclesSocketStore((state) => state.vehicles);
  const selectedGroup = useVehiclesSocketStore(
    (state) => state.selectedGroup
  );
  const selectedCompany = useVehiclesSocketStore(
    (state) => state.selectedCompany
  );
  const companyUser = useCompaniesStore((state) => state.companyUser);
  const user = useUserStore((state) => state.user);
  const userRoleId = user?.role_id || localStorage.getItem("userRoleId") || "";
  const isReseller = userRoleId === ADMIN_RESELLER_ROLE_ID;
  const groups = useGroupsStore((state) => state.groups);
  const { isMonitor, devices: monitorDeviceIds } = useIsMonitor();

  const stats = useMemo(() => {
    // Primero filtrar por compañía
    let companyVehicles = vehicles;

    // Si es monitor, mostrar dispositivos asignados directamente o por grupos
    if (isMonitor) {
      // Obtener dispositivos de grupos donde el monitorista está asignado
      const monitoristaUserId = localStorage.getItem("userId");
      const monitoristaGroups = groups.filter((g) =>
        g.monitorista_ids?.includes(monitoristaUserId || "")
      );
      const groupDeviceIds = monitoristaGroups.flatMap((g) => g.devices_ids || []);

      // Combinar dispositivos asignados directamente con dispositivos de grupos
      const allowedDeviceIds = new Set([
        ...(monitorDeviceIds || []),
        ...groupDeviceIds,
      ]);

      // Si tiene dispositivos permitidos, filtrar; si no, mostrar vacío
      if (allowedDeviceIds.size > 0) {
        companyVehicles = companyVehicles.filter((v) => allowedDeviceIds.has(v.id));
      } else {
        companyVehicles = [];
      }
    }

    if (isReseller) {
      // Reseller: filtrar por compañía seleccionada
      if (!selectedCompany) {
        return {
          total: 0,
          driving: 0,
          ralenti: 0,
          stopped: 0,
          offline: 0,
        };
      }
      companyVehicles = companyVehicles.filter((v) => v.companyId === selectedCompany);
    } else if (companyUser) {
      // Carrier: filtrar por su propia compañía
      companyVehicles = companyVehicles.filter((v) => v.companyId === companyUser);
    }

    // Filtrar por grupo usando devices_ids
    let vehiclesInGroup = companyVehicles;
    if (selectedGroup !== "all") {
      const selectedGroupData = groups.find((g) => g.id === selectedGroup);
      const groupDeviceIds = selectedGroupData?.devices_ids || [];
      vehiclesInGroup = companyVehicles.filter((v) => groupDeviceIds.includes(v.id));
    }

    let driving = 0, ralenti = 0, stopped = 0, offline = 0;
    for (const v of vehiclesInGroup) {
      switch (v.status) {
        case "driving": driving++; break;
        case "ralenti": ralenti++; break;
        case "stopped": stopped++; break;
        case "offline": offline++; break;
      }
    }
    return { total: vehiclesInGroup.length, driving, ralenti, stopped, offline };
  }, [vehicles, selectedGroup, selectedCompany, companyUser, isReseller, groups, isMonitor, monitorDeviceIds]);

  return stats;
};

// Hook para obtener el estado de conexión y carga
export const useSocketConnectionStatus = () => {
  const loading = useVehiclesSocketStore((state) => state.loading);
  const isConnected = useVehiclesSocketStore((state) => state.isConnected);
  const error = useVehiclesSocketStore((state) => state.error);
  const lastReconnect = useVehiclesSocketStore((state) => state.lastReconnect);
  const isInitialized = useVehiclesSocketStore((state) => state.isInitialized);
  const awaitingCompanySelection = useVehiclesSocketStore((state) => state.awaitingCompanySelection);

  return {
    loading,
    isConnected,
    error,
    lastReconnect,
    isInitialized,
    awaitingCompanySelection,
  };
};

// Hook para saber si el usuario es reseller (por ROL)
export const useIsSocketReseller = () => {
  const user = useUserStore((state) => state.user);
  const userRoleId = user?.role_id || localStorage.getItem("userRoleId") || "";
  return userRoleId === ADMIN_RESELLER_ROLE_ID;
};

// Hook para obtener datos en tiempo real de un device (speed, fuel, battery, address)
// Usa shallow comparison para evitar re-renders innecesarios
export const useRealtimeDeviceData = (deviceId: string | undefined) => {
  return useVehiclesSocketStore(
    useShallow((state) => {
      if (!deviceId) return { speed: null, fuel: null, battery: null, odometer: null, engineHours: null, address: "", hasData: false };
      const index = state.vehiclesMap.get(deviceId);
      if (index === undefined) return { speed: null, fuel: null, battery: null, odometer: null, engineHours: null, address: "", hasData: false };
      const v = state.vehicles[index];
      return {
        speed: v?.speed ?? null,
        fuel: v?.fuel ?? null,
        battery: v?.battery ?? null,
        odometer: v?.odometer ?? null,
        engineHours: v?.engineHours ?? null,
        address: v?.address ?? "",
        hasData: true,
      };
    })
  );
};
